import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import type { PublicUser } from "@book2class/shared";
import { api, ApiError } from "../api";
import { homeFor, useAuth } from "../auth";
import { AuthLayout } from "../components/layouts";
import { Field, Spinner } from "../components/ui";

type Errors = Record<string, string>;
const fieldErrors = (err: unknown): [string, Errors] =>
  err instanceof ApiError ? [err.message, err.fields] : ["Algo deu errado. Tente novamente.", {}];

const safeRedirect = (v: string | null) => (v && v.startsWith("/") && !v.startsWith("//") ? v : null);

export const GOOGLE_ERRORS: Record<string, string> = {
  google_sem_conta: "Não foi possível registrar a solicitação de acesso. Tente de novo em instantes.",
  google_cancelado: "O login com o Google foi cancelado.",
  google_sessao: "A sessão de login expirou. Tente entrar com o Google novamente.",
  google_falha: "O Google não confirmou o login. Tente novamente.",
  conta_inativa: "Conta inativa. Procure o administrador da instituição.",
  google_convite: "Este convite expirou ou já foi utilizado. Peça um novo convite ao professor.",
  google_convite_email: "Este convite foi enviado para outro e-mail. Entre com a conta Google desse e-mail.",
  google_aceite: "Para criar a conta, marque o aceite dos Termos de Uso e da Política de Privacidade.",
};

/** Logotipo do Google usado nos botões de login. */
export function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}

export function LoginPage() {
  const { user, setUser, refresh } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [google, setGoogle] = useState(false);
  useEffect(() => {
    api
      .get<{ google?: boolean }>("/auth/config")
      .then((c) => setGoogle(Boolean(c.google)))
      .catch(() => undefined);
  }, []);
  const googleError = GOOGLE_ERRORS[params.get("erro") ?? ""];

  if (user) return <Navigate to={safeRedirect(params.get("voltar")) ?? homeFor(user)} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api.post<{ user: PublicUser }>("/auth/login", form);
      setUser(res.user);
      await refresh();
      navigate(safeRedirect(params.get("voltar")) ?? homeFor(res.user), { replace: true });
    } catch (err) {
      const [msg, fields] = fieldErrors(err);
      setError(msg);
      setErrors(fields);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Entrar" subtitle="Acesse seus cursos e ebooks.">
      <form className="form" onSubmit={submit} noValidate>
        {params.get("senha") === "redefinida" && (
          <p className="alert ok">Senha redefinida. Entre com a nova senha.</p>
        )}
        {(error || googleError) && <p className="alert error">{error || googleError}</p>}
        {google && (
          <>
            <a
              className="btn ghost block google-btn"
              href={`/api/auth/google${safeRedirect(params.get("voltar")) ? `?voltar=${encodeURIComponent(params.get("voltar")!)}` : ""}`}
            >
              <GoogleLogo />
              Entrar com o Google
            </a>
            <div className="divider-or">ou com e-mail e senha</div>
          </>
        )}
        <Field
          label="E-mail"
          type="email"
          autoComplete="email"
          required
          value={form.email}
          error={errors.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <Field
          label="Senha"
          type="password"
          autoComplete="current-password"
          required
          value={form.password}
          error={errors.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
        <button className="btn block" disabled={busy}>
          {busy ? "Entrando…" : "Entrar"}
        </button>
        <Link to="/esqueci-senha" style={{ fontSize: "0.92rem" }}>
          Esqueci minha senha
        </Link>
      </form>
      <p className="auth-foot">
        Ainda sem conta? <Link to="/cadastro">Criar conta</Link>
      </p>
    </AuthLayout>
  );
}

type LegalDoc = { id: string; type: string; version: string; title: string; content: string };

export function RegisterPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<"student" | "teacher">(
    params.get("perfil") === "professor" ? "teacher" : "student",
  );
  const [allowTeacher, setAllowTeacher] = useState(true);
  const [google, setGoogle] = useState(false);
  const [docs, setDocs] = useState<LegalDoc[]>([]);
  // Cadastro vindo de um link de convite: a conta também pode nascer pelo Google.
  const inviteToken = /^\/convite\/([^/?#]+)/.exec(safeRedirect(params.get("voltar")) ?? "")?.[1] ?? "";
  const [form, setForm] = useState({
    institutionSlug: params.get("instituicao") ?? "",
    institutionName: "",
    name: "",
    email: "",
    password: "",
    acceptTerms: false,
  });
  const [error, setError] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ allowInstitutionSignup: boolean; google?: boolean }>("/auth/config")
      .then((c) => {
        setGoogle(Boolean(c.google));
        setAllowTeacher(c.allowInstitutionSignup);
        if (!c.allowInstitutionSignup) setMode("student");
      })
      .catch(() => undefined);
    api
      .get<LegalDoc[]>("/legal/current")
      .then(setDocs)
      .catch(() => undefined);
  }, []);

  if (user) return <Navigate to={safeRedirect(params.get("voltar")) ?? homeFor(user)} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setErrors({});
    try {
      const { institutionSlug, institutionName, ...common } = form;
      const res =
        mode === "teacher"
          ? await api.post<{ user: PublicUser }>("/auth/register-institution", { institutionName, ...common })
          : institutionSlug.trim()
            ? await api.post<{ user: PublicUser }>("/auth/register", { institutionSlug, ...common })
            : await api.post<{ user: PublicUser }>("/auth/request-access", common);
      setUser(res.user);
      navigate(safeRedirect(params.get("voltar")) ?? homeFor(res.user), { replace: true });
    } catch (err) {
      const [msg, fields] = fieldErrors(err);
      setError(msg);
      setErrors(fields);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Criar conta"
      subtitle={
        mode === "teacher" ? "Transforme seus ebooks em cursos." : "Acesse os cursos da sua instituição."
      }
    >
      <form className="form" onSubmit={submit} noValidate>
        {allowTeacher && (
          <div className="radio-cards" role="radiogroup" aria-label="Tipo de conta">
            <label className="radio-card">
              <input
                type="radio"
                name="mode"
                checked={mode === "student"}
                onChange={() => setMode("student")}
              />
              <span>
                <strong>Sou aluno(a)</strong>
                <br />
                Vou estudar em uma instituição já cadastrada.
              </span>
            </label>
            <label className="radio-card">
              <input
                type="radio"
                name="mode"
                checked={mode === "teacher"}
                onChange={() => setMode("teacher")}
              />
              <span>
                <strong>Sou professor(a) ou instituição</strong>
                <br />
                Vou criar e publicar cursos a partir de ebooks.
              </span>
            </label>
          </div>
        )}
        {error && <p className="alert error">{error}</p>}
        {mode === "teacher" ? (
          <Field
            label="Nome da instituição ou do seu projeto"
            value={form.institutionName}
            error={errors.institutionName}
            onChange={(e) => setForm({ ...form, institutionName: e.target.value })}
          />
        ) : (
          <Field
            label="Código da instituição (opcional)"
            hint="Informado pelo seu professor. Sem código, a conta fica aguardando a liberação do administrador."
            autoCapitalize="none"
            value={form.institutionSlug}
            error={errors.institutionSlug}
            onChange={(e) => setForm({ ...form, institutionSlug: e.target.value })}
          />
        )}
        <Field
          label="Nome completo"
          autoComplete="name"
          value={form.name}
          error={errors.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Field
          label="E-mail"
          type="email"
          autoComplete="email"
          value={form.email}
          error={errors.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <Field
          label="Senha"
          type="password"
          autoComplete="new-password"
          hint="Mínimo de 8 caracteres, com letras e números."
          value={form.password}
          error={errors.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
        {docs.length > 0 && (
          <details>
            <summary style={{ cursor: "pointer", fontSize: "0.9rem", color: "var(--link)", fontWeight: 600 }}>
              Ler termos de uso e política de privacidade
            </summary>
            {docs.map((d) => (
              <div key={d.id} style={{ marginTop: 10 }}>
                <div className="label">
                  {d.title} · versão {d.version}
                </div>
                <div className="legal-text">{d.content}</div>
              </div>
            ))}
          </details>
        )}
        <div className="field">
          <label className="check" style={{ fontWeight: 400 }}>
            <input
              type="checkbox"
              checked={form.acceptTerms}
              aria-invalid={errors.acceptTerms ? true : undefined}
              onChange={(e) => setForm({ ...form, acceptTerms: e.target.checked })}
            />
            <span>Li e aceito os termos de uso e a política de privacidade.</span>
          </label>
          {errors.acceptTerms && <span className="error">{errors.acceptTerms}</span>}
        </div>
        <button className="btn block" disabled={busy}>
          {busy ? "Criando conta…" : "Criar conta"}
        </button>
        {google && mode === "student" && (
          <>
            <div className="divider-or">ou, depois de marcar o aceite acima</div>
            <a
              className="btn ghost block google-btn"
              href={`/api/auth/google?${inviteToken ? `convite=${inviteToken}&` : ""}${form.acceptTerms ? "aceite=1" : ""}`}
            >
              <GoogleLogo />
              Criar conta com o Google
            </a>
          </>
        )}
      </form>
      <p className="auth-foot">
        Já tem conta?{" "}
        <Link
          to={`/entrar${params.get("voltar") ? `?voltar=${encodeURIComponent(params.get("voltar")!)}` : ""}`}
        >
          Entrar
        </Link>
      </p>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <AuthLayout title="Recuperar senha" subtitle="Enviaremos um link para você criar uma nova senha.">
      {sent ? (
        <p className="alert ok">{sent}</p>
      ) : (
        <form
          className="form"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError("");
            try {
              const res = await api.post<{ message: string }>("/auth/forgot-password", { email });
              setSent(res.message);
            } catch (err) {
              setError(fieldErrors(err)[1].email ?? fieldErrors(err)[0]);
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field
            label="E-mail"
            type="email"
            autoComplete="email"
            value={email}
            error={error}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button className="btn block" disabled={busy}>
            {busy ? "Enviando…" : "Enviar link"}
          </button>
        </form>
      )}
      <p className="auth-foot">
        <Link to="/entrar">Voltar ao login</Link>
      </p>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);

  return (
    <AuthLayout title="Nova senha" subtitle="Escolha uma senha para acessar a plataforma.">
      {!token ? (
        <p className="alert error">Link incompleto. Abra novamente o link recebido por e-mail.</p>
      ) : (
        <form
          className="form"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            // eslint-disable-next-line security/detect-possible-timing-attacks -- comparação no navegador entre os dois campos digitados pela própria pessoa
            if (password !== confirm) {
              setErrors({ confirm: "As senhas digitadas são diferentes." });
              return;
            }
            setBusy(true);
            setError("");
            try {
              await api.post("/auth/reset-password", { token, password });
              navigate("/entrar?senha=redefinida", { replace: true });
            } catch (err) {
              const [msg, fields] = fieldErrors(err);
              setError(msg);
              setErrors(fields);
            } finally {
              setBusy(false);
            }
          }}
        >
          {error && <p className="alert error">{error}</p>}
          <Field
            label="Nova senha"
            type="password"
            autoComplete="new-password"
            hint="Mínimo de 8 caracteres, com letras e números."
            value={password}
            error={errors.password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Field
            label="Confirme a nova senha"
            type="password"
            autoComplete="new-password"
            value={confirm}
            error={errors.confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <button className="btn block" disabled={busy}>
            {busy ? "Salvando…" : "Salvar nova senha"}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}

export function LegalPage() {
  const [docs, setDocs] = useState<LegalDoc[] | null>(null);
  useEffect(() => {
    api
      .get<LegalDoc[]>("/legal/current")
      .then(setDocs)
      .catch(() => setDocs([]));
  }, []);
  return (
    <main className="page" style={{ maxWidth: 760 }}>
      <h1>Documentos legais</h1>
      {!docs ? (
        <Spinner />
      ) : (
        docs.map((d) => (
          <section key={d.id} className="card card-pad" style={{ marginTop: 16 }}>
            <h2>{d.title}</h2>
            <p className="segment-meta">Versão {d.version}</p>
            <div style={{ whiteSpace: "pre-wrap" }}>{d.content}</div>
          </section>
        ))
      )}
    </main>
  );
}
